/**
 * SwipeStack component for managing the swipeable user stack
 */

import type { PublicUserProfile } from "@fitties/shared";
import logger from "@logger";

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { TinderCardDragMove } from "react-tinder-card";
import TinderCard from "react-tinder-card";
import { resolveDiscoveryUserId } from "../services/discoveryService";
import {
  DEFAULT_SWIPE_STACK_TUNING,
  isSwipeStackTuningEnabled,
  resolveSwipeThreshold,
} from "../tuning/swipeStackTuning";
import { useSwipeStackTuning } from "../tuning/useSwipeStackTuning";
import type { Match, SwipeStackProps, TinderCardAPI } from "../types";
import { SwipeStackTuningPanel } from "./SwipeStackTuningPanel";
import { UserCard } from "./UserCard";

function useViewportWidth(): number {
  const [width, setWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 390));

  useLayoutEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return width;
}

export type SwipeDragState = {
  dx: number;
  progress: number;
  direction: "left" | "right" | null;
} | null;

function resolveStackScale(isNextCard: boolean, dragState: SwipeDragState): number | undefined {
  if (!isNextCard) {
    return undefined;
  }
  return 0.95 + (dragState?.progress ?? 0) * 0.05;
}

type SwipeStackCardProps = {
  user: PublicUserProfile;
  index: number;
  currentIndex: number;
  activeTuning: typeof DEFAULT_SWIPE_STACK_TUNING;
  swipeThreshold: number;
  dragState: SwipeDragState;
  interactionLocked: boolean;
  onDragMove: (gesture: TinderCardDragMove) => void;
  onSwipe: (direction: "left" | "right" | "up" | "down", userId: string) => void;
  onOutOfFrame: (direction: "left" | "right" | "up" | "down", userId: string) => void;
  onMatchFromCard: (matchData: { userId: string; user: PublicUserProfile }) => void;
  setCardRef: (index: number, el: TinderCardAPI | null) => void;
  activeCardSwipeRef: React.RefObject<TinderCardAPI | null>;
};

function SwipeStackCard({
  user,
  index,
  currentIndex,
  activeTuning,
  swipeThreshold,
  dragState,
  interactionLocked,
  onDragMove,
  onSwipe,
  onOutOfFrame,
  onMatchFromCard,
  setCardRef,
  activeCardSwipeRef,
}: SwipeStackCardProps) {
  const isActive = index === currentIndex;
  const isNextCard = index === currentIndex + 1;
  const rowId = resolveDiscoveryUserId(user) ?? "";
  const stackScale = resolveStackScale(isNextCard, dragState);

  return (
    <div
      className={`absolute inset-0 ${isActive ? "z-10" : "z-0"}`}
      style={stackScale === undefined ? undefined : { transform: `scale(${stackScale})` }}
      data-testid={isNextCard ? "swipe-stack-next-card" : isActive ? "swipe-stack-active-card" : undefined}
    >
      <TinderCard
        ref={(el) => setCardRef(index, el)}
        flickOnSwipe={activeTuning.flickOnSwipe}
        swipeRequirementType={activeTuning.swipeRequirementType}
        swipeThreshold={swipeThreshold}
        onSwipe={(direction) => onSwipe(direction, rowId)}
        onCardLeftScreen={(direction) => onOutOfFrame(direction, rowId)}
        {...(isActive ? { onDragMove } : {})}
        preventSwipe={interactionLocked ? ["left", "right", "up", "down"] : ["up", "down"]}
        className="absolute inset-0"
      >
        <UserCard
          user={user}
          isActive={isActive}
          interactionLocked={interactionLocked}
          onSwipe={() => {}}
          onMatch={onMatchFromCard}
          tinderCardRef={isActive ? activeCardSwipeRef : null}
        />
      </TinderCard>
    </div>
  );
}

/**
 * SwipeStack component for the discovery feature
 *
 * @example
 * ```tsx
 * <SwipeStack
 *   users={users}
 *   onSwipe={(userId, direction) => handleSwipe(userId, direction)}
 *   onMatch={(match) => handleMatch(match)}
 * />
 * ```
 */
export function SwipeStack({ users, onSwipe, onMatch, interactionLocked = false }: SwipeStackProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localUsers, setLocalUsers] = useState(users);
  const [dragState, setDragState] = useState<SwipeDragState>(null);
  const viewportWidth = useViewportWidth();
  const tuningEnabled = isSwipeStackTuningEnabled();
  const { tuning, setTuning, resetTuning } = useSwipeStackTuning();
  const activeTuning = tuningEnabled ? tuning : DEFAULT_SWIPE_STACK_TUNING;
  const swipeThreshold = resolveSwipeThreshold(activeTuning, viewportWidth);
  const activeCardTuningKey = `${activeTuning.swipeRequirementType}-${activeTuning.flickOnSwipe}`;

  const childRefs = useRef<Record<number, TinderCardAPI | null>>({});
  const activeCardSwipeRef = useRef<TinderCardAPI | null>(null);

  const handleDragMove = useCallback(
    (gesture: TinderCardDragMove) => {
      if (!gesture) {
        setDragState(null);
        return;
      }
      const progress = Math.min(1, Math.abs(gesture.dx) / swipeThreshold);
      const direction = gesture.dx > 0 ? "right" : gesture.dx < 0 ? "left" : null;
      setDragState({ dx: gesture.dx, progress, direction });
    },
    [swipeThreshold]
  );

  useLayoutEffect(() => {
    if (users.length === 0) {
      setLocalUsers([]);
      setCurrentIndex(0);
      childRefs.current = {};
      return;
    }
    setLocalUsers(users);
    setCurrentIndex(0);
    childRefs.current = {};
  }, [users]);

  const swiped = (direction: "left" | "right" | "up" | "down", userId: string) => {
    if (interactionLocked) {
      return;
    }
    onSwipe(userId, direction as "left" | "right");
    setDragState(null);
    setCurrentIndex((prev) => prev + 1);
  };

  const outOfFrame = (_direction: "left" | "right" | "up" | "down", userId: string) => {
    logger.debug(`${userId} left the screen`);
  };

  const handleMatchFromCard = (matchData: { userId: string; user: PublicUserProfile }) => {
    const match: Match = {
      id: `temp-${Date.now()}`,
      users: [matchData.userId],
      created_time: new Date(),
    };
    onMatch(match);
  };

  const setCardRef = useCallback(
    (index: number, el: TinderCardAPI | null) => {
      childRefs.current[index] = el;
      if (index === currentIndex) {
        activeCardSwipeRef.current = el;
      }
    },
    [currentIndex]
  );

  const handleRetry = () => {
    setCurrentIndex(0);
    childRefs.current = {};
  };

  if (currentIndex >= localUsers.length) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-4 font-mono text-2xl font-bold text-gray-900">You've seen everyone!</h2>
          <p className="text-gray-600 mb-6">Check back later for new profiles, or try adjusting your preferences.</p>
          <button
            type="button"
            onClick={handleRetry}
            className="bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
            data-testid="retry-button"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  if (localUsers.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-2 font-mono text-xl font-semibold text-gray-900">No users to display</h2>
          <p className="text-gray-600">Something went wrong loading the user data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-0 w-full flex-1 overflow-hidden" data-testid="swipe-stack">
      {localUsers.map((user, index) => {
        if (index < currentIndex || index > currentIndex + activeTuning.stackLookahead) {
          return null;
        }

        const rowId = resolveDiscoveryUserId(user) ?? "";
        const isActive = index === currentIndex;
        const cardKey = isActive ? `${rowId}-${activeCardTuningKey}` : rowId || `stack-${index}`;

        return (
          <SwipeStackCard
            key={cardKey}
            user={user}
            index={index}
            currentIndex={currentIndex}
            activeTuning={activeTuning}
            swipeThreshold={swipeThreshold}
            dragState={dragState}
            interactionLocked={interactionLocked}
            onDragMove={handleDragMove}
            onSwipe={swiped}
            onOutOfFrame={outOfFrame}
            onMatchFromCard={handleMatchFromCard}
            setCardRef={setCardRef}
            activeCardSwipeRef={activeCardSwipeRef}
          />
        );
      })}

      {tuningEnabled ? <SwipeStackTuningPanel tuning={tuning} onChange={setTuning} onReset={resetTuning} /> : null}
    </div>
  );
}
